// Audio toggle control for Navbar.
// - Left icon: master SFX toggle (🔊 / 🔇).
// - Hover reveals a small popover with Ambient Dojo toggle + volume.
// Persists state via lib/audio.js (localStorage).

import { useEffect, useState } from "react";
import { Volume2, VolumeX, Music, Wind } from "lucide-react";
import { getPrefs, toggleSfx, toggleAmbient, setVolume, subscribe, playSfx } from "@/lib/audio";

export default function AudioToggle() {
  const [p, setP] = useState(getPrefs());

  useEffect(() => subscribe(setP), []);

  const handleToggleSfx = () => {
    toggleSfx();
  };
  const handleToggleAmbient = () => {
    toggleAmbient();
    if (!p.ambientEnabled) setTimeout(() => playSfx("click"), 30);
  };

  const isOn = p.sfxEnabled;
  const Icon = isOn ? Volume2 : VolumeX;
  const color = isOn ? "#22C55E" : "rgba(255,255,255,0.45)";

  return (
    <div
      data-testid="audio-toggle"
      className="bc-audio-wrap"
      style={{ position: "relative", display: "inline-flex" }}
    >
      <button
        type="button"
        data-testid="audio-toggle-btn"
        onClick={handleToggleSfx}
        aria-label={isOn ? "Disattiva suoni" : "Attiva suoni"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          background: isOn ? "rgba(34,197,94,0.08)" : "var(--bc-panel-elev, rgba(255,255,255,0.03))",
          border: `1px solid ${isOn ? "rgba(34,197,94,0.4)" : "var(--bc-border, rgba(255,255,255,0.07))"}`,
          color,
          cursor: "pointer",
          transition: "all 0.15s",
          boxShadow: isOn ? "0 0 10px -4px #22C55E" : "none",
        }}
      >
        <Icon size={13} />
      </button>

      <div
        className="bc-audio-pop"
        role="menu"
        style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          right: 0,
          minWidth: 240,
          background: "#0B0B0E",
          border: "1px solid rgba(255,255,255,0.14)",
          padding: "0.85rem",
          zIndex: 60,
          boxShadow: "0 12px 40px -8px var(--bc-panel, rgba(0,0,0,0.75))",
          pointerEvents: "none",
          opacity: 0,
          transform: "translateY(-4px)",
          transition: "opacity 140ms ease, transform 140ms ease",
        }}
      >
        <div
          style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontSize: "0.68rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.5)",
            marginBottom: "0.6rem",
          }}
        >
          Audio Dojo
        </div>

        {/* SFX switch */}
        <Row
          icon={<Music size={12} />}
          label="Effetti sonori"
          on={p.sfxEnabled}
          onClick={handleToggleSfx}
          color="#22C55E"
          testid="audio-row-sfx"
        />

        {/* Ambient switch */}
        <Row
          icon={<Wind size={12} />}
          label="Ambient dojo"
          on={p.ambientEnabled}
          onClick={handleToggleAmbient}
          color="#A78BFA"
          testid="audio-row-ambient"
        />

        {/* Volume slider */}
        <div style={{ marginTop: "0.75rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.6rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
            }}
          >
            <span>Volume</span>
            <span style={{ color: "#22C55E", fontFamily: "'JetBrains Mono',monospace" }}>{Math.round(p.volume * 100)}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={p.volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            data-testid="audio-volume-slider"
            style={{ width: "100%", accentColor: "#22C55E", cursor: "pointer" }}
          />
        </div>
      </div>

      <style>{`
        .bc-audio-wrap:hover .bc-audio-pop,
        .bc-audio-wrap:focus-within .bc-audio-pop {
          opacity: 1 !important;
          transform: translateY(0) !important;
          pointer-events: auto !important;
        }
      `}</style>
    </div>
  );
}

function Row({ icon, label, on, onClick, color, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "0.5rem 0.25rem",
        background: "transparent",
        border: "none",
        borderTop: "1px solid var(--bc-border, rgba(255,255,255,0.07))",
        color: "rgba(255,255,255,0.85)",
        cursor: "pointer",
        fontFamily: "'Barlow Condensed',sans-serif",
        fontSize: "0.78rem",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: on ? color : "rgba(255,255,255,0.7)" }}>
        {icon}
        {label}
      </span>
      <span
        style={{
          display: "inline-block",
          width: 30,
          height: 14,
          position: "relative",
          background: on ? `color-mix(in srgb, ${color} 35%, var(--bc-panel, #09090B))` : "var(--bc-border, rgba(255,255,255,0.07))",
          border: `1px solid ${on ? color : "var(--bc-border, rgba(255,255,255,0.1))"}`,
          transition: "all 0.15s",
          boxShadow: on ? `0 0 8px -2px ${color}` : "none",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 1,
            left: on ? 16 : 1,
            width: 10,
            height: 10,
            background: on ? color : "rgba(255,255,255,0.4)",
            transition: "left 0.15s, background 0.15s",
          }}
        />
      </span>
    </button>
  );
}
